import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatISOStringToReadable } from '../../../../utils/dateUtils';
import { MODAL_MODES } from '../../../../constants/enums';
import { translateMembershipLevel } from '../../../../utils/stranslateUltils';
import '../css/Modal.css';
import { FaExclamationTriangle } from "react-icons/fa";

const CustomerModal = ({ customer, mode, onSave, onDelete, onClose }) => {
  const { t } = useTranslation();
  const [editedCustomer, setEditedCustomer] = useState(customer);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    setEditedCustomer(customer);
    setValidationErrors([]);
  }, [customer]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setEditedCustomer(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setEditedCustomer(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear validation errors when user starts editing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const validateCustomer = (customer) => {
    const errors = [];

    if (!customer.customerName.trim()) {
      errors.push(t('admin.customer.modal.validation.name'));
    }

    if (!customer?.phoneNumber?.trim()) {
      errors.push(t('admin.customer.modal.validation.phone'));
    }
    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const handleSave = () => {
    const validation = validateCustomer(editedCustomer);
    
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    onSave(editedCustomer);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderValidationErrors = () => {
    if (validationErrors.length === 0) return null;

    return (
      <div className="validation-errors">
        <h4>{t('admin.base_modal.validation_title')}</h4>
        <ul>
          {validationErrors.map((error, index) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      </div>
    );
  };

  const renderViewMode = () => (
    <div className="modal-content">
      <div className="modal-header">
        <h2>{t('admin.customer.modal.view.title')}</h2>
        <button className="close-btn" onClick={onClose} aria-label={t('admin.base_modal.close')}>×</button>
      </div>
      
      <div className="modal-body">
        <div className="detail-section">
          <h3>{t('admin.customer.modal.view.customer_info')}</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>{t('admin.customer.modal.view.customer_id')}:</label>
              <span>{customer.customerId}</span>
            </div>
            <div className="detail-item">
              <label>{t('admin.customer.modal.view.name')}:</label>
              <span>{customer.customerName}</span>
            </div>
            <div className="detail-item">
              <label>{t('admin.customer.modal.view.phone')}:</label>
              <span>{customer.phoneNumber}</span>
            </div>
            <div className="detail-item">
              <label>{t('admin.customer.modal.view.membership')}:</label>
              <span>{translateMembershipLevel(t,customer.membershipLevel)}</span>
            </div>
            <div className="detail-item">
              <label>{t('admin.customer.modal.view.loyalty_points')}:</label>
              <span>{customer.loyaltyPoints}</span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h3>{t('admin.customer.modal.view.timestamps')}</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>{t('admin.customer.modal.view.created_at')}:</label>
              <span>{formatISOStringToReadable(customer.createdAt)}</span>
            </div>
            <div className="detail-item">
              <label>{t('admin.customer.modal.view.updated_at')}:</label>
              <span>{formatISOStringToReadable(customer.updatedAt)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>{t('admin.base_modal.close')}</button>
      </div>
    </div>
  );

  const renderEditMode = () => (
    <div className="modal-content">
      <div className="modal-header">
        <h2>{t('admin.customer.modal.edit.title')}</h2>
        <button className="close-btn" onClick={onClose} aria-label={t('admin.base_modal.close')}>×</button>
      </div>
      
      <div className="modal-body">
        {renderValidationErrors()}

        <div className="edit-section">
          <h3>{t('admin.customer.modal.edit.customer_info')}</h3>
          <div className="form-grid">
            <div className="form-item">
              <label>{t('admin.customer.modal.edit.name')}:</label>
              <input
                type="text"
                value={editedCustomer.customerName}
                onChange={(e) => handleInputChange('customerName', e.target.value)}
                className="form-input"
                placeholder={t('admin.customer.modal.edit.placeholders.name')}
              />
            </div>
            <div className="form-item">
              <label>{t('admin.customer.modal.edit.phone')}:</label>
              <input
                type="text"
                value={editedCustomer.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                className="form-input"
                placeholder={t('admin.customer.modal.edit.placeholders.phone')}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>{t('admin.base_modal.cancel')}</button>
        <button className="btn btn-primary" onClick={handleSave}>{t('admin.base_modal.save_changes')}</button>
      </div>
    </div>
  );

  const renderDeleteMode = () => (
    <div className="modal-content">
      <div className="modal-header">
        <h2>{t('admin.customer.modal.delete.title')}</h2>
        <button className="close-btn" onClick={onClose} aria-label={t('admin.base_modal.close')}>×</button>
      </div>
      
      <div className="modal-body">
        <div className="delete-confirmation">
          <div className="warning-icon"><FaExclamationTriangle style={{ fontSize: '48px', color: '#dc2626' }} /></div>
          <h3>{t('admin.customer.modal.delete.confirmation')}</h3>
          <p>
            {t('admin.customer.modal.delete.warning', {
              name: customer.customerName,
              id: customer.customerId
            })}
          </p>
          <p className="warning-text">{t('admin.customer.modal.delete.warning_text')}</p>
        </div>
      </div>

      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>{t('admin.base_modal.cancel')}</button>
        <button className="btn btn-danger" onClick={onDelete}>{t('admin.base_modal.delete')}</button>
      </div>
    </div>
  );

  const renderModalContent = () => {
    switch (mode) {
      case MODAL_MODES.VIEW:
        return renderViewMode();
      case MODAL_MODES.EDIT:
        return renderEditMode();
      case MODAL_MODES.DELETE:
        return renderDeleteMode();
      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-wrapper" onClick={(e) => e.stopPropagation()}>
        {renderModalContent()}
      </div>
    </div>
  );
};

export default CustomerModal;